import { useState, useEffect, useRef, useCallback } from "react";
import { printOrder } from "./printOrder";
import ManualOrderForm from "./ManualOrderForm";
import EquipeTab from "./EquipeTab";
import CozinhaTab from "./CozinhaTab";
import ConfigTab from "./ConfigTab";
import MesasTab from "./MesasTab";

// ============================================
// MISE BY NEUROTECH-X — PAINEL DE GESTÃO
// React Frontend · Login + 3 Abas: Pedidos, Cardápio, Caixa
// Integrado com API real · WebSocket tempo real
// ============================================

const API = window.location.origin;

const C = {
  bg: "#FAF7F2",
  surface: "#FFFFFF",
  surfaceAlt: "#F5F0E8",
  card: "#FFFFFF",
  border: "#E8E0D4",
  borderLight: "#F0EBE3",
  text: "#1A1A1A",
  textMid: "#5C564E",
  textMuted: "#9A9389",
  accent: "#E8593C",
  accentSoft: "rgba(232,89,60,0.08)",
  accentHover: "#D44A2E",
  gold: "#D4920A",
  goldSoft: "rgba(212,146,10,0.08)",
  green: "#2D8C3C",
  greenSoft: "rgba(45,140,60,0.08)",
  blue: "#2563EB",
  blueSoft: "rgba(37,99,235,0.08)",
  red: "#DC2626",
  redSoft: "rgba(220,38,38,0.08)",
};

const STATUS = {
  recebido: { label: "Recebido", color: C.accent, bg: C.accentSoft, icon: "●" },
  em_preparo: { label: "Em preparo", color: C.gold, bg: C.goldSoft, icon: "●" },
  pronto: { label: "Pronto", color: C.gold, bg: C.goldSoft, icon: "●" },
  saiu_entrega: { label: "Saiu p/ entrega", color: C.blue, bg: C.blueSoft, icon: "●" },
  entregue: { label: "Entregue", color: C.green, bg: C.greenSoft, icon: "●" },
  retirado: { label: "Retirado", color: C.green, bg: C.greenSoft, icon: "●" },
  servido: { label: "Servido", color: C.green, bg: C.greenSoft, icon: "●" },
  cancelado: { label: "Cancelado", color: C.red, bg: C.redSoft, icon: "●" },
};

const PAYMENT_LABELS = { dinheiro: "💵 Dinheiro", cartao: "💳 Cartão", pix: "📱 PIX" };

// ============================================
// API HELPER
// ============================================
const api = {
  getToken: () => sessionStorage.getItem("mise_token"),
  getUser: () => { try { return JSON.parse(sessionStorage.getItem("mise_user")); } catch { return null; } },
  getTenant: () => { try { return JSON.parse(sessionStorage.getItem("mise_tenant")); } catch { return null; } },
  headers: () => ({ "Content-Type": "application/json", Authorization: `Bearer ${sessionStorage.getItem("mise_token")}` }),
  async fetch(path, opts = {}) {
    const res = await fetch(`${API}${path}`, { ...opts, headers: { ...api.headers(), ...opts.headers } });
    if (res.status === 401) { sessionStorage.clear(); window.location.reload(); return null; }
    return res.json();
  },
  get: (path) => api.fetch(path),
  post: (path, body) => api.fetch(path, { method: "POST", body: JSON.stringify(body) }),
  put: (path, body) => api.fetch(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: (path, body) => api.fetch(path, { method: "PATCH", body: JSON.stringify(body) }),
  del: (path) => api.fetch(path, { method: "DELETE" }),
};

// ============================================
// BOWL LOGO COMPONENT
// ============================================
const BowlLogo = ({ size = 48 }) => (
  <svg width={size} height={size} viewBox="-90 -90 180 180" xmlns="http://www.w3.org/2000/svg">
    <path d="M-60 0 Q-60 55 0 55 Q60 55 60 0" fill="none" stroke="#1A1A1A" strokeWidth="5.5" strokeLinecap="round"/>
    <line x1="-26" y1="58" x2="26" y2="58" stroke="#1A1A1A" strokeWidth="4" strokeLinecap="round"/>
    <path d="M-22 -18 Q-28 -42 -20 -55" fill="none" stroke="#E8593C" strokeWidth="3.5" strokeLinecap="round" opacity="0.9">
      <animate attributeName="d" values="M-22 -18 Q-28 -42 -20 -55;M-22 -21 Q-30 -46 -20 -60;M-22 -18 Q-28 -42 -20 -55" dur="2.8s" repeatCount="indefinite"/>
    </path>
    <path d="M0 -22 Q-6 -50 2 -65" fill="none" stroke="#D4920A" strokeWidth="4.5" strokeLinecap="round" opacity="1">
      <animate attributeName="d" values="M0 -22 Q-6 -50 2 -65;M0 -25 Q-8 -54 2 -71;M0 -22 Q-6 -50 2 -65" dur="2.2s" repeatCount="indefinite"/>
    </path>
    <path d="M22 -18 Q16 -42 24 -55" fill="none" stroke="#E8593C" strokeWidth="3.5" strokeLinecap="round" opacity="0.9">
      <animate attributeName="d" values="M22 -18 Q16 -42 24 -55;M22 -21 Q14 -46 24 -60;M22 -18 Q16 -42 24 -55" dur="3s" repeatCount="indefinite"/>
    </path>
  </svg>
);

// ============================================
// NOTIFICATION SOUND
// ============================================
const useNotificationSound = () => {
  const audioCtx = useRef(null);
  const play = useCallback(() => {
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioCtx.current;
      const osc1 = ctx.createOscillator(); const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      osc1.type = "sine"; osc1.frequency.setValueAtTime(587, ctx.currentTime);
      osc2.type = "sine"; osc2.frequency.setValueAtTime(880, ctx.currentTime);
      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
      osc1.connect(gain); osc2.connect(gain); gain.connect(ctx.destination);
      osc1.start(ctx.currentTime); osc2.start(ctx.currentTime + 0.15);
      osc1.stop(ctx.currentTime + 0.8); osc2.stop(ctx.currentTime + 0.95);
    } catch(e) {}
  }, []);
  return play;
};

// ============================================
// LOGIN PAGE
// ============================================
const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) { setError("Preencha todos os campos"); return; }
    setLoading(true); setError("");
    try {
      const res = await fetch(`${API}/api/auth/login`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Erro ao fazer login"); setLoading(false); return; }
      sessionStorage.setItem("mise_token", data.token);
      sessionStorage.setItem("mise_user", JSON.stringify(data.user));
      sessionStorage.setItem("mise_tenant", JSON.stringify(data.tenant));
      onLogin(data);
    } catch (e) {
      setError("Erro de conexão com o servidor");
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: `linear-gradient(135deg, ${C.bg} 0%, #F0E8DC 50%, ${C.bg} 100%)`,
      fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{
        background: C.surface, borderRadius: 20, padding: "48px 44px", width: 400,
        boxShadow: "0 8px 40px rgba(0,0,0,0.08)", border: `1px solid ${C.border}`,
      }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <BowlLogo size={72} />
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 32, fontWeight: 700, letterSpacing: 10, color: C.text, marginTop: 8 }}>MISE</div>
          <div style={{ fontSize: 10, letterSpacing: 4, color: C.textMuted, marginTop: 2 }}>by NEUROTECH-X</div>
          <div style={{ fontSize: 13, color: C.textMid, marginTop: 16 }}>Tudo no lugar. Tudo no tempo.</div>
        </div>

        {error && (
          <div style={{ background: C.redSoft, color: C.red, padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, textAlign: "center" }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: C.textMid, marginBottom: 6, display: "block" }}>E-mail</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="seu@email.com"
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              style={{
                width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}`,
                fontSize: 14, fontFamily: "'DM Sans', sans-serif", background: C.bg, outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, color: C.textMid, marginBottom: 6, display: "block" }}>Senha</label>
            <input value={password} onChange={e => setPassword(e.target.value)} type="password" placeholder="••••••••"
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              style={{
                width: "100%", padding: "12px 14px", borderRadius: 10, border: `1px solid ${C.border}`,
                fontSize: 14, fontFamily: "'DM Sans', sans-serif", background: C.bg, outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <button onClick={handleLogin} disabled={loading} style={{
            padding: "13px 0", borderRadius: 10, border: "none", background: C.accent, color: "#fff",
            fontSize: 15, fontWeight: 600, cursor: loading ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif",
            boxShadow: `0 4px 16px ${C.accent}40`, marginTop: 6, opacity: loading ? 0.7 : 1,
            transition: "all 0.2s",
          }}>
            {loading ? "Entrando..." : "Entrar"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// SIDEBAR
// ============================================
const Sidebar = ({ activeTab, setActiveTab, activeBadge, onLogout, tenant, user }) => {
  const [showPwModal, setShowPwModal] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const handleChangePw = async () => {
    if (!pwForm.current || !pwForm.new) { setPwMsg("Preencha todos os campos"); return; }
    if (pwForm.new.length < 4) { setPwMsg("Nova senha minimo 4 caracteres"); return; }
    if (pwForm.new !== pwForm.confirm) { setPwMsg("Senhas nao conferem"); return; }
    setPwLoading(true);
    try {
      const res = await fetch(window.location.origin + "/api/auth/change-password", {
        method: "PATCH", headers: { "Content-Type": "application/json", Authorization: "Bearer " + sessionStorage.getItem("mise_token") },
        body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.new }),
      });
      const data = await res.json();
      if (data.success) { setPwMsg("Senha alterada com sucesso!"); setPwForm({ current: "", new: "", confirm: "" }); setTimeout(() => { setShowPwModal(false); setPwMsg(""); }, 1500); }
      else { setPwMsg(data.error || "Erro ao alterar"); }
    } catch { setPwMsg("Erro de conexao"); }
    setPwLoading(false);
  };
  const allTabs = [
    { id: "pedidos", icon: "📋", label: "Pedidos", badge: activeBadge, roles: ["owner", "garcom", "caixa"] },
    { id: "cozinha", icon: "🔥", label: "Cozinha", badge: activeBadge, roles: ["owner", "cozinha"] },
    { id: "mesas", icon: "🍽️", label: "Mesas", roles: ["owner", "garcom"] },
    { id: "cardapio", icon: "📖", label: "Cardápio", roles: ["owner"] },
    { id: "caixa", icon: "💰", label: "Caixa", roles: ["owner", "caixa"] },
    { id: "equipe", icon: "👥", label: "Equipe", roles: ["owner"] },
    { id: "config", icon: "⚙️", label: "Configurações", roles: ["owner"] },
  ];
  const tabs = allTabs.filter(t => t.roles.includes(user?.role || "owner"));
  return (
    <div style={{ width: 230, background: C.surface, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
      <div style={{
        padding: "28px 20px 24px", borderBottom: `1px solid ${C.border}`,
        display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
        background: "linear-gradient(180deg, rgba(232,89,60,0.04) 0%, transparent 100%)",
      }}>
        <BowlLogo size={64} />
        <div style={{ textAlign: "center", marginTop: 2 }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 26, fontWeight: 700, letterSpacing: 8, color: C.text }}>MISE</div>
          <div style={{ fontSize: 9, letterSpacing: 3, color: C.textMuted, marginTop: 1 }}>by NEUROTECH-X</div>
        </div>
      </div>
      <div style={{ padding: "16px 12px", flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
        {tabs.map(t => (
          <div key={t.id} onClick={() => setActiveTab(t.id)} style={{
            display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10,
            cursor: "pointer", transition: "all 0.2s",
            background: activeTab === t.id ? C.accentSoft : "transparent",
            color: activeTab === t.id ? C.accent : C.textMid,
            fontWeight: activeTab === t.id ? 600 : 400,
            fontFamily: "'DM Sans', sans-serif", fontSize: 14,
          }}>
            <span style={{ fontSize: 18 }}>{t.icon}</span>
            <span style={{ flex: 1 }}>{t.label}</span>
            {t.badge > 0 && <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, background: C.accent, color: "#fff" }}>{t.badge}</span>}
          </div>
        ))}
      </div>
      <div style={{ padding: "12px 12px 4px" }}>
        <div onClick={() => { const link = window.location.origin + "/cardapio/" + (tenant?.slug || ""); navigator.clipboard.writeText(link).then(() => { const el = document.getElementById("copy-msg"); if(el){ el.style.opacity=1; setTimeout(()=>el.style.opacity=0, 2000); } }); }} style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10,
          cursor: "pointer", color: C.green, fontSize: 13, fontWeight: 500,
          transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif",
          background: C.greenSoft, border: "1px solid " + C.green + "22",
        }}>
          <span style={{ fontSize: 16 }}>📎</span> Meu Cardapio
        </div>
        <div id="copy-msg" style={{ fontSize: 11, color: C.green, textAlign: "center", marginTop: 4, opacity: 0, transition: "opacity 0.3s" }}>Link copiado!</div>
      </div>
      <div style={{ padding: "4px 12px 8px" }}>
        <div onClick={onLogout} style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10,
          cursor: "pointer", color: C.red, fontSize: 13, fontWeight: 500,
          transition: "all 0.2s", fontFamily: "'DM Sans', sans-serif",
        }}>
          <span style={{ fontSize: 16 }}>🚪</span> Sair
        </div>
      </div>
      <div style={{ padding: "12px 20px 16px", borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.textMuted }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green }}/>
          {tenant?.name || "Restaurante"}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ opacity: 0.6, fontSize: 11 }}>{user?.name || "Usuário"}</div>
          <div onClick={() => { setShowPwModal(true); setPwMsg(""); setPwForm({ current: "", new: "", confirm: "" }); }} style={{ cursor: "pointer", fontSize: 14, opacity: 0.5, transition: "opacity 0.2s" }} onMouseEnter={e => e.target.style.opacity=1} onMouseLeave={e => e.target.style.opacity=0.5} title="Trocar senha">🔐</div>
        </div>
      </div>
      {showPwModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }} onClick={() => setShowPwModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 12, padding: 28, width: 360, border: "1px solid " + C.border }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 16, fontFamily: "'Playfair Display',serif" }}>Trocar Senha</h3>
            {pwMsg && <div style={{ padding: "8px 12px", borderRadius: 8, background: pwMsg.includes("sucesso") ? C.greenSoft : C.redSoft, color: pwMsg.includes("sucesso") ? C.green : C.red, fontSize: 13, marginBottom: 12 }}>{pwMsg}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
              <input type="password" placeholder="Senha atual" value={pwForm.current} onChange={e => setPwForm({...pwForm, current: e.target.value})} style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid " + C.border, background: C.bg, color: C.text, fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" }} />
              <input type="password" placeholder="Nova senha" value={pwForm.new} onChange={e => setPwForm({...pwForm, new: e.target.value})} style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid " + C.border, background: C.bg, color: C.text, fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" }} />
              <input type="password" placeholder="Confirmar nova senha" value={pwForm.confirm} onChange={e => setPwForm({...pwForm, confirm: e.target.value})} onKeyDown={e => e.key === "Enter" && handleChangePw()} style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid " + C.border, background: C.bg, color: C.text, fontSize: 14, fontFamily: "'DM Sans',sans-serif", outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleChangePw} disabled={pwLoading} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans',sans-serif", opacity: pwLoading ? 0.6 : 1 }}>{pwLoading ? "Alterando..." : "Alterar Senha"}</button>
              <button onClick={() => setShowPwModal(false)} style={{ padding: "10px 16px", borderRadius: 8, border: "1px solid " + C.border, background: "#fff", color: C.text, fontSize: 14, cursor: "pointer", fontFamily: "'DM Sans',sans-serif" }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// PEDIDOS TAB
// ============================================
const PedidosTab = ({ orders, setOrders, loadOrders, tenant }) => {
  const [showManualForm, setShowManualForm] = useState(false);
  const [filter, setFilter] = useState("ativos");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [updating, setUpdating] = useState(null);
  const playSound = useNotificationSound();

  const filteredOrders = orders.filter(o => {
    if (filter === "ativos") return !["entregue", "retirado", "servido", "cancelado", "finalizado"].includes(o.status);
    if (filter === "entregues") return o.status === "entregue" || o.status === "retirado" || o.status === "servido" || o.status === "finalizado";
    return true;
  });

  const updateStatus = async (orderId, newStatus) => {
    setUpdating(orderId);
    await api.patch(`/api/orders/${orderId}/status`, { status: newStatus });
    await loadOrders();
    setUpdating(null);
  };

  const confirmPayment = async (orderId) => {
    setUpdating(orderId);
    await api.patch(`/api/orders/${orderId}/pay`, {});
    await loadOrders();
    setUpdating(null);
  };

  const handlePrint = (order) => printOrder(order, tenant, api.get);

  const getNextStatus = (order) => {
    if (order.order_type === 'retirada') {
      return { recebido: "pronto", pronto: "retirado" }[order.status] || null;
    }
    return { recebido: "pronto", pronto: "saiu_entrega", saiu_entrega: "entregue" }[order.status] || null;
  };

  const getNextLabel = (order) => {
    if (order.order_type === 'retirada') {
      return { recebido: "Marcar Pronto", pronto: "✅ Marcar Retirado" }[order.status] || null;
    }
    return { recebido: "Marcar Pronto", pronto: "Saiu p/ Entrega", saiu_entrega: "✅ Marcar Entregue" }[order.status] || null;
  };

  const needsPaymentFirst = (order) => {
    const finalStatuses = ['entregue', 'retirado'];
    const next = getNextStatus(order);
    return finalStatuses.includes(next) && order.payment_status !== 'pago';
  };

  const timeSince = (dateStr) => {
    const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diff < 1) return "agora";
    if (diff < 60) return `${diff}min`;
    return `${Math.floor(diff / 60)}h${diff % 60}min`;
  };

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Pedidos</h2>
            <button onClick={() => setShowManualForm(true)} style={{ padding: "7px 16px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer", boxShadow: "0 2px 8px " + C.accent + "40" }}>+ Novo Pedido</button>
          </div>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>{filteredOrders.length} pedido{filteredOrders.length !== 1 ? "s" : ""}</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[{k:"ativos",l:"Ativos"},{k:"entregues",l:"Entregues"},{k:"todos",l:"Todos"}].map(f => (
            <button key={f.k} onClick={() => setFilter(f.k)} style={{
              padding: "7px 16px", borderRadius: 8, border: `1px solid ${filter === f.k ? C.accent : C.border}`,
              background: filter === f.k ? C.accentSoft : C.surface, color: filter === f.k ? C.accent : C.textMid,
              fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            }}>{f.l}</button>
          ))}
          <button onClick={playSound} style={{
            padding: "7px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
            background: C.surface, cursor: "pointer", fontSize: 13, color: C.textMid,
          }}>🔔 Testar som</button>
        </div>
      </div>

      {filteredOrders.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: C.textMuted }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 16, fontWeight: 500 }}>Nenhum pedido {filter === "ativos" ? "ativo" : filter === "entregues" ? "entregue" : ""}</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>Os pedidos aparecerão aqui em tempo real</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filteredOrders.map(order => {
            const s = STATUS[order.status] || STATUS.recebido;
            const isExpanded = expandedOrder === order.id;
            const items = order.items || [];
            return (
              <div key={order.id} style={{
                background: C.card, border: `1px solid ${order.status === "recebido" ? C.accent : C.border}`,
                borderRadius: 14, overflow: "hidden", transition: "all 0.25s",
                boxShadow: order.status === "recebido" ? `0 0 0 1px ${C.accent}20, 0 4px 20px ${C.accent}10` : "0 1px 4px rgba(0,0,0,0.04)",
              }}>
                <div onClick={() => setExpandedOrder(isExpanded ? null : order.id)} style={{
                  padding: "16px 20px", cursor: "pointer", display: "grid",
                  gridTemplateColumns: "80px 1fr 120px 100px 100px auto", alignItems: "center", gap: 12,
                }}>
                  <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, fontWeight: 700, color: C.text }}>#{order.order_number}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{order.customer_name}</div>
                    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>{items.map(i => `${i.quantity}x ${i.name}`).join(", ")}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, color: C.text, fontFamily: "'DM Sans', sans-serif" }}>R$ {Number(order.total).toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: C.textMuted }}>{PAYMENT_LABELS[order.payment_method] || order.payment_method}</div>
                  </div>
                  <div style={{ textAlign: "center", display: "flex", flexDirection: "column", gap: 4, alignItems: "center" }}>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <span style={{ fontSize: 11, padding: "4px 12px", borderRadius: 20, background: s.bg, color: s.color, fontWeight: 600 }}>{s.label}</span>
                      {order.payment_status === "pago" && <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 10, background: "rgba(45,140,60,0.12)", color: "#2D8C3C", fontWeight: 700, letterSpacing: 0.5 }}>PAGO ✓</span>}
                    </div>
                    <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 10, background: order.order_source === "manual" ? "rgba(212,146,10,0.12)" : "rgba(37,99,235,0.08)", color: order.order_source === "manual" ? "#D4920A" : "#2563EB", fontWeight: 600, letterSpacing: 0.5 }}>{order.order_source === "manual" ? "MANUAL" : "WHATSAPP"}</span>
                  </div>
                  <div style={{ fontSize: 12, color: C.textMuted, textAlign: "center" }}>{timeSince(order.created_at)}</div>
                  <span style={{ fontSize: 16, color: C.textMuted, transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "rotate(0)" }}>▾</span>
                </div>

                {isExpanded && (
                  <div style={{ padding: "0 20px 20px", borderTop: `1px solid ${C.borderLight}`, paddingTop: 16 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Itens do pedido</div>
                        {items.map((item, i) => (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: i < items.length - 1 ? `1px solid ${C.borderLight}` : "none" }}>
                            <div>
                              <span style={{ fontWeight: 600, color: C.accent, marginRight: 6 }}>{item.quantity}x</span>
                              <span style={{ color: C.text }}>{item.name}</span>
                              {item.notes && <div style={{ fontSize: 11, color: C.gold, marginTop: 2 }}>📝 {item.notes}</div>}
                            </div>
                            <span style={{ fontWeight: 600, color: C.text }}>R$ {Number(item.item_total).toFixed(2)}</span>
                          </div>
                        ))}
                        {Number(order.delivery_fee) > 0 && (
                          <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, color: C.textMuted }}>
                            <span>Taxa de entrega</span><span>R$ {Number(order.delivery_fee).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Dados da entrega</div>
                        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>
                          {order.customer_address && <div>📍 {order.customer_address}</div>}
                          <div>📱 {order.customer_phone}</div>
                          <div>{PAYMENT_LABELS[order.payment_method]}{order.payment_change_for ? ` · Troco p/ R$${order.payment_change_for}` : ""}</div>
                          <div style={{ marginTop: 4, fontSize: 12, color: C.textMuted }}>Tipo: {order.order_type === "delivery" ? "🛵 Delivery" : "🏪 Retirada"}</div>
                        </div>
                      </div>
                    </div>
                    {!getNextStatus(order) && (
                      <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
                        <button onClick={() => handlePrint(order)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>🖨️ Imprimir</button>
                      </div>
                    )}
                    {getNextStatus(order) && (
                      <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        <button onClick={() => handlePrint(order)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 13, fontWeight: 500, cursor: "pointer" }}>🖨️ Imprimir</button>
                        <button onClick={() => updateStatus(order.id, "cancelado")} disabled={updating === order.id} style={{
                          padding: "8px 18px", borderRadius: 8, border: `1px solid ${C.border}`,
                          background: C.surface, color: C.red, fontSize: 13, fontWeight: 500, cursor: "pointer",
                        }}>Cancelar</button>
                        
                        <button onClick={() => {
                          if (needsPaymentFirst(order)) { alert("Confirme o pagamento antes de finalizar o pedido"); return; }
                          updateStatus(order.id, getNextStatus(order));
                        }} disabled={updating === order.id} style={{
                          padding: "8px 24px", borderRadius: 8, border: "none",
                          background: needsPaymentFirst(order) ? C.border : C.accent, color: "#fff", fontSize: 13, fontWeight: 600,
                          cursor: needsPaymentFirst(order) ? "not-allowed" : "pointer",
                          boxShadow: needsPaymentFirst(order) ? "none" : `0 2px 8px ${C.accent}40`,
                          opacity: updating === order.id ? 0.6 : needsPaymentFirst(order) ? 0.5 : 1,
                        }}>{updating === order.id ? "..." : getNextLabel(order)}</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {showManualForm && <ManualOrderForm api={api} onClose={() => setShowManualForm(false)} onCreated={(order) => { setShowManualForm(false); loadOrders(); handlePrint(order); }} />}
    </div>
  );
};

// ============================================
// CARDÁPIO TAB
// ============================================
const CardapioTab = () => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showNewItem, setShowNewItem] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatIcon, setNewCatIcon] = useState("🍽️");
  const [saving, setSaving] = useState(false);

  // Form state for new/edit item
  const [itemForm, setItemForm] = useState({ name: "", description: "", price: "", category_id: "" });

  const loadData = async () => {
    setLoading(true);
    const [cats, its] = await Promise.all([api.get("/api/menu/categories"), api.get("/api/menu/items")]);
    if (cats) setCategories(cats);
    if (its) setItems(its);
    if (cats && cats.length > 0 && !activeCat) setActiveCat(cats[0].id);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const toggleItem = async (id, currentState) => {
    await api.put(`/api/menu/items/${id}`, { is_active: !currentState });
    setItems(prev => prev.map(i => i.id === id ? { ...i, is_active: !i.is_active } : i));
  };

  const saveNewCategory = async () => {
    if (!newCatName.trim()) return;
    setSaving(true);
    const cat = await api.post("/api/menu/categories", { name: newCatName, icon: newCatIcon, sort_order: categories.length + 1 });
    if (cat) { setCategories(prev => [...prev, cat]); setActiveCat(cat.id); }
    setNewCatName(""); setNewCatIcon("🍽️"); setShowNewCat(false); setSaving(false);
  };

  const saveNewItem = async () => {
    if (!itemForm.name || !itemForm.price) return;
    setSaving(true);
    const catId = itemForm.category_id || activeCat;
    const item = await api.post("/api/menu/items", {
      name: itemForm.name, description: itemForm.description,
      price: parseFloat(itemForm.price), category_id: catId,
    });
    if (item) { setItems(prev => [...prev, item]); }
    setItemForm({ name: "", description: "", price: "", category_id: "" });
    setShowNewItem(false); setSaving(false);
  };

  const updateItem = async () => {
    if (!itemForm.name || !itemForm.price) return;
    setSaving(true);
    const updated = await api.put(`/api/menu/items/${editingItem}`, {
      name: itemForm.name, description: itemForm.description,
      price: parseFloat(itemForm.price),
    });
    if (updated) { setItems(prev => prev.map(i => i.id === editingItem ? { ...i, ...updated } : i)); }
    setEditingItem(null); setItemForm({ name: "", description: "", price: "", category_id: "" });
    setSaving(false);
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Excluir este item?")) return;
    await api.del(`/api/menu/items/${id}`);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const filtered = items.filter(i => i.category_id === activeCat);

  if (loading) return <div style={{ padding: 60, textAlign: "center", color: C.textMuted }}>Carregando cardápio...</div>;

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>Cardápio</h2>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>{items.filter(i => i.is_active).length} itens ativos · {items.filter(i => !i.is_active).length} inativos</p>
        </div>
        <button onClick={() => { setShowNewItem(true); setEditingItem(null); setItemForm({ name: "", description: "", price: "", category_id: activeCat }); }} style={{
          padding: "10px 20px", borderRadius: 10, border: "none", background: C.accent,
          color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
          boxShadow: `0 2px 8px ${C.accent}40`, display: "flex", alignItems: "center", gap: 6,
        }}>+ Novo item</button>
      </div>

      {/* Category tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        {categories.map(cat => (
          <button key={cat.id} onClick={() => setActiveCat(cat.id)} style={{
            padding: "10px 18px", borderRadius: 10, border: `1px solid ${activeCat === cat.id ? C.accent : C.border}`,
            background: activeCat === cat.id ? C.accentSoft : C.surface,
            color: activeCat === cat.id ? C.accent : C.textMid,
            fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif",
            display: "flex", alignItems: "center", gap: 6,
          }}>
            <span style={{ fontSize: 16 }}>{cat.icon}</span> {cat.name}
            <span style={{ fontSize: 11, opacity: 0.6 }}>({items.filter(i => i.category_id === cat.id).length})</span>
          </button>
        ))}
        {showNewCat ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input value={newCatIcon} onChange={e => setNewCatIcon(e.target.value)} style={{ width: 40, padding: "8px 4px", borderRadius: 8, border: `1px solid ${C.border}`, textAlign: "center", fontSize: 16 }} />
            <input value={newCatName} onChange={e => setNewCatName(e.target.value)} placeholder="Nome" onKeyDown={e => e.key === "Enter" && saveNewCategory()}
              style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }} />
            <button onClick={saveNewCategory} disabled={saving} style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: C.green, color: "#fff", fontSize: 12, cursor: "pointer" }}>✓</button>
            <button onClick={() => setShowNewCat(false)} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, fontSize: 12, cursor: "pointer" }}>✕</button>
          </div>
        ) : (
          <button onClick={() => setShowNewCat(true)} style={{
            padding: "10px 16px", borderRadius: 10, border: `1px dashed ${C.border}`,
            background: "transparent", color: C.textMuted, fontSize: 13, cursor: "pointer",
          }}>+ Categoria</button>
        )}
      </div>

      {/* New/Edit item modal */}
      {(showNewItem || editingItem) && (
        <div style={{
          background: C.card, border: `1px solid ${C.accent}`, borderRadius: 14, padding: 22, marginBottom: 20,
          boxShadow: `0 4px 20px ${C.accent}15`,
        }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 16 }}>
            {editingItem ? "Editar item" : "Novo item"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: C.textMid, marginBottom: 4, display: "block" }}>Nome</label>
              <input value={itemForm.name} onChange={e => setItemForm({ ...itemForm, name: e.target.value })}
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: C.textMid, marginBottom: 4, display: "block" }}>Preço (R$)</label>
              <input value={itemForm.price} onChange={e => setItemForm({ ...itemForm, price: e.target.value })} type="number" step="0.01"
                style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" }} />
            </div>
          </div>
          <div style={{ marginTop: 12 }}>
            <label style={{ fontSize: 12, color: C.textMid, marginBottom: 4, display: "block" }}>Descrição</label>
            <input value={itemForm.description} onChange={e => setItemForm({ ...itemForm, description: e.target.value })}
              style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box" }} />
          </div>
          <div style={{ marginTop: 16, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={() => { setShowNewItem(false); setEditingItem(null); }} style={{
              padding: "8px 18px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.textMid, fontSize: 13, cursor: "pointer",
            }}>Cancelar</button>
            <button onClick={editingItem ? updateItem : saveNewItem} disabled={saving} style={{
              padding: "8px 24px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>{saving ? "Salvando..." : "Salvar"}</button>
          </div>
        </div>
      )}

      {/* Items grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
        {filtered.map(item => (
          <div key={item.id} style={{
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 18,
            opacity: item.is_active ? 1 : 0.5, transition: "all 0.25s", position: "relative",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text, fontFamily: "'DM Sans', sans-serif" }}>{item.name}</div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, lineHeight: 1.4 }}>{item.description}</div>
              </div>
              <div onClick={() => toggleItem(item.id, item.is_active)} style={{
                width: 44, height: 24, borderRadius: 12, cursor: "pointer", padding: 2,
                background: item.is_active ? C.green : C.border, transition: "background 0.2s",
                display: "flex", alignItems: "center",
                justifyContent: item.is_active ? "flex-end" : "flex-start",
              }}>
                <div style={{ width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.15)", transition: "all 0.2s" }}/>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
              <span style={{ fontSize: 20, fontWeight: 700, color: C.accent, fontFamily: "'Playfair Display', serif" }}>
                R$ {Number(item.price).toFixed(2)}
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={() => {
                  setEditingItem(item.id); setShowNewItem(false);
                  setItemForm({ name: item.name, description: item.description || "", price: item.price, category_id: item.category_id });
                }} style={{
                  padding: "5px 14px", borderRadius: 6, border: `1px solid ${C.border}`,
                  background: C.surface, color: C.textMid, fontSize: 12, cursor: "pointer",
                }}>Editar</button>
                <button onClick={() => deleteItem(item.id)} style={{
                  padding: "5px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
                  background: C.surface, color: C.red, fontSize: 12, cursor: "pointer",
                }}>✕</button>
              </div>
            </div>
            {!item.is_active && (
              <div style={{
                position: "absolute", top: 10, right: 10, fontSize: 10, padding: "2px 8px",
                borderRadius: 4, background: C.redSoft, color: C.red, fontWeight: 600,
              }}>INATIVO</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================
// CAIXA TAB
// ============================================
const CaixaTab = () => {
  const [summary, setSummary] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [loading, setLoading] = useState(true);
  const [caixaStatus, setCaixaStatus] = useState({ open: false, session: null });
  const [selectedDate, setSelectedDate] = useState("");
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [valorAbertura, setValorAbertura] = useState("");
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [pending, setPending] = useState([]);
  const [frente, setFrente] = useState(null);
  const [payMethod, setPayMethod] = useState("dinheiro");
  const [valorRecebido, setValorRecebido] = useState("");
  const [parteTarget, setParteTarget] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [sessDetail, setSessDetail] = useState(null);

  const todayStr = new Date().toISOString().slice(0, 10);

  const loadCaixa = async (date) => {
    setLoading(true);
    const dateParam = date ? `?date=${date}` : "";
    const [sum, comp, status, pend] = await Promise.all([
      api.get(`/api/cash/today${dateParam}`),
      api.get(`/api/cash/completed${dateParam}`),
      api.get("/api/cash/status"),
      api.get("/api/cash/pending")
    ]);
    if (sum) setSummary(sum);
    if (comp) setCompleted(comp);
    if (status) setCaixaStatus(status);
    if (pend && Array.isArray(pend)) setPending(pend);
    setLoading(false);
  };

  const loadSessions = async () => {
    const data = await api.get("/api/cash/sessions");
    if (data) setSessions(data);
  };

  useEffect(() => { loadCaixa(selectedDate); }, [selectedDate]);

  const handleOpenCaixa = async () => {
    const res = await api.post("/api/cash/open", { valor_abertura: Number(valorAbertura) || 0 });
    if (res && !res.error) {
      setShowOpenModal(false);
      setValorAbertura("");
      loadCaixa(selectedDate);
    } else {
      alert(res?.error || "Erro ao abrir caixa");
    }
  };

  const openFrente = async (p) => {
    const det = await api.get("/api/cash/pending/" + p.id);
    if (det && det.order) {
      setFrente(det);
      setPayMethod(det.order.payment_method || "dinheiro");
      setValorRecebido("");
      setParteTarget(null);
    }
  };

  const handleConfirmPay = async () => {
    if (!frente) return;
    let res;
    if (parteTarget) res = await api.post("/api/pagamentos-mesa/" + parteTarget.id + "/pay", { payment_method: payMethod });
    else res = await api.patch("/api/orders/" + frente.order.id + "/pay", { payment_method: payMethod });
    if (res && res.error) alert(res.error);
    setShowPayModal(false); setFrente(null); setParteTarget(null); setValorRecebido("");
    loadCaixa(selectedDate);
  };

  const handleCloseCaixa = async () => {
    if (!window.confirm("Tem certeza que deseja FECHAR o caixa? Os totais serão congelados.")) return;
    const res = await api.post("/api/cash/close", {});
    if (res && !res.error) {
      loadCaixa(selectedDate);
    } else {
      alert((res?.error || "Erro ao fechar caixa") + (res?.pendentes && res.pendentes.length ? "\nPedidos a cobrar: #" + res.pendentes.join(", #") : ""));
    }
  };

  if (loading || !summary) return <div style={{ padding: 60, textAlign: "center", color: C.textMuted }}>Carregando caixa...</div>;

  const totalRevenue = Number(summary.total_revenue) || 0;
  const totalOrders = Number(summary.total_orders) || 0;
  const avgTicket = Number(summary.avg_ticket) || 0;
  const activeOrders = Number(summary.active_orders) || 0;
  const totalCash = Number(summary.total_cash) || 0;
  const totalCard = Number(summary.total_card) || 0;
  const totalPix = Number(summary.total_pix) || 0;
  const totalDeliveryFees = Number(summary.total_delivery_fees) || 0;
  const isToday = !selectedDate || selectedDate === todayStr;

  return (
    <div style={{ padding: 28 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, fontWeight: 700, color: C.text, margin: 0 }}>
            {isToday ? "Caixa do Dia" : "Caixa — Histórico"}
          </h2>
          <p style={{ fontSize: 13, color: C.textMuted, margin: "4px 0 0" }}>
            {selectedDate ? new Date(selectedDate + "T12:00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} style={{
            padding: "8px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
            background: C.surface, fontSize: 13, color: C.textMid, cursor: "pointer"
          }} />
          {selectedDate && <button onClick={() => setSelectedDate("")} style={{
            padding: "8px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
            background: C.surface, cursor: "pointer", fontSize: 13, color: C.accent
          }}>Hoje</button>}
          <button onClick={() => loadCaixa(selectedDate)} style={{
            padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`,
            background: C.surface, cursor: "pointer", fontSize: 13, color: C.textMid
          }}>🔄</button>
          <button onClick={() => { loadSessions(); setShowHistory(!showHistory); }} style={{
            padding: "8px 16px", borderRadius: 8, border: `1px solid ${showHistory ? C.accent : C.border}`,
            background: showHistory ? C.accentSoft : C.surface, cursor: "pointer", fontSize: 13, color: showHistory ? C.accent : C.textMid, fontWeight: showHistory ? 600 : 400
          }}>📊 Histórico</button>
        </div>
      </div>

      {/* Painel Histórico de Sessões */}
      {showHistory && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 22, marginBottom: 20 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 14, fontFamily: "'Playfair Display', serif" }}>Sessões anteriores</div>
          {sessions.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: C.textMuted, fontSize: 13 }}>Nenhuma sessão encontrada</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {sessions.filter(s => s.closed_at).map(s => {
                const sDate = new Date(s.opened_at).toISOString().slice(0, 10);
                const isSelected = selectedDate === sDate;
                return (
                  <div key={s.id} onClick={() => { setSelectedDate(sDate); setShowHistory(false); }} style={{
                    display: "grid", gridTemplateColumns: "1fr 120px 100px 100px 100px 110px 50px", alignItems: "center",
                    padding: "12px 16px", borderRadius: 10, cursor: "pointer",
                    background: isSelected ? C.accentSoft : C.surface,
                    border: `1px solid ${isSelected ? C.accent : C.borderLight}`,
                    transition: "all 0.2s"
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                      {new Date(s.opened_at).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })}
                    </span>
                    <span style={{ fontSize: 12, color: C.textMuted }}>
                      {new Date(s.opened_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })} - {new Date(s.closed_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                    <span style={{ fontSize: 12, color: C.green }}>💵 {Number(s.total_dinheiro).toFixed(0)}</span>
                    <span style={{ fontSize: 12, color: C.blue }}>💳 {Number(s.total_cartao).toFixed(0)}</span>
                    <span style={{ fontSize: 12, color: C.accent }}>📱 {Number(s.total_pix).toFixed(0)}</span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: C.green, textAlign: "right" }}>R$ {Number(s.total_geral).toFixed(2)}</span>
                    <button onClick={async (ev) => { ev.stopPropagation(); const det = await api.get("/api/cash/sessions/" + s.id + "/orders"); if (det && det.session) setSessDetail(det); }} style={{ marginLeft: 8, padding: "4px 10px", borderRadius: 6, border: "1px solid " + C.border, background: C.surface, cursor: "pointer", fontSize: 12 }}>📋</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Barra status caixa */}
      {isToday && <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderRadius: 12, marginBottom: 20, background: caixaStatus.open ? C.greenSoft : C.accentSoft, border: `1px solid ${caixaStatus.open ? C.green : C.accent}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>{caixaStatus.open ? "🟢" : "🔴"}</span>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text }}>{caixaStatus.open ? "Caixa Aberto" : "Caixa Fechado"}</span>
          {caixaStatus.session && <span style={{ fontSize: 12, color: C.textMuted, marginLeft: 8 }}>desde {new Date(caixaStatus.session.opened_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</span>}
        </div>
        {caixaStatus.open
          ? <button onClick={handleCloseCaixa} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: C.accent, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Fechar Caixa</button>
          : <button onClick={() => setShowOpenModal(true)} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: C.green, color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>Abrir Caixa</button>
        }
      </div>}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 28 }}>
        {[
          { label: "Faturamento", value: `R$ ${totalRevenue.toFixed(2)}`, color: C.green, bg: C.greenSoft },
          { label: "Pedidos pagos", value: totalOrders, color: C.accent, bg: C.accentSoft },
          { label: "Ticket médio", value: `R$ ${avgTicket.toFixed(2)}`, color: C.gold, bg: C.goldSoft },
          { label: "Pedidos ativos", value: activeOrders, color: C.blue, bg: C.blueSoft },
        ].map((stat, i) => (
          <div key={i} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "20px 22px" }}>
            <div style={{ fontSize: 11, color: C.textMuted, textTransform: "uppercase", letterSpacing: 1.2, marginBottom: 8 }}>{stat.label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, color: stat.color, fontFamily: "'Playfair Display', serif" }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* A cobrar / Frente de Caixa */}
      {isToday && pending.length > 0 && (() => {
        const totalCobrar = frente ? (parteTarget ? Number(parteTarget.valor) : Number(frente.order.total)) : 0;
        const troco = (Number(valorRecebido) || 0) - totalCobrar;
        const temPartes = frente && frente.pagamentos && frente.pagamentos.length > 0;
        return (
        <div style={{ display: "grid", gridTemplateColumns: "230px 1fr", gap: 16, marginBottom: 20, alignItems: "start" }}>
          <div style={{ background: C.card, border: "1px solid " + C.gold, borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10, fontFamily: "'Playfair Display', serif" }}>A cobrar ({pending.length})</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {pending.map(p => {
                const sel = frente && frente.order && frente.order.id === p.id;
                return (
                <div key={p.id} onClick={() => openFrente(p)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 12px", borderRadius: 10, cursor: "pointer", background: sel ? C.accentSoft : C.surface, border: "1px solid " + (sel ? C.accent : C.borderLight) }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: C.text }}>#{p.order_number}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.gold }}>R$ {Number(p.total).toFixed(2)}</span>
                </div>
              );})}
            </div>
          </div>
          <div style={{ background: C.card, border: "1px solid " + C.border, borderRadius: 14, padding: 22, minHeight: 160 }}>
            {!frente ? (
              <div style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 13 }}>Selecione um pedido ao lado para cobrar</div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                  <div>
                    <span style={{ fontSize: 18, fontWeight: 700, color: C.text, fontFamily: "'Playfair Display', serif" }}>Pedido #{frente.order.order_number}</span>
                    <span style={{ fontSize: 13, color: C.textMid, marginLeft: 10 }}>{frente.order.customer_name || "-"}</span>
                    <span style={{ fontSize: 11, color: C.textMuted, marginLeft: 10, textTransform: "uppercase" }}>{frente.order.order_type} · {frente.order.status}</span>
                  </div>
                  <button onClick={() => setFrente(null)} style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 16, color: C.textMuted }}>✕</button>
                </div>
                <div style={{ borderTop: "1px solid " + C.borderLight, borderBottom: "1px solid " + C.borderLight, padding: "10px 0", marginBottom: 14 }}>
                  {(frente.items || []).map((it, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.textMid, padding: "3px 0" }}>
                      <span>{it.quantity}x {it.name}</span>
                      <span>R$ {Number(it.item_total).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                {temPartes && (
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 8 }}>Conta dividida</div>
                    {frente.pagamentos.map(pg => (
                      <div key={pg.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", borderRadius: 8, marginBottom: 6, background: C.surface, border: "1px solid " + (parteTarget && parteTarget.id === pg.id ? C.accent : C.borderLight) }}>
                        <span style={{ fontSize: 13, color: C.text }}>{pg.descricao || "Parte"}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 13, fontWeight: 700, color: C.gold }}>R$ {Number(pg.valor_total).toFixed(2)}</span>
                          {pg.payment_status === "pago"
                            ? <span style={{ fontSize: 11, fontWeight: 700, color: C.green }}>PAGO ✓</span>
                            : <button onClick={() => { setParteTarget({ id: pg.id, valor: pg.valor_total, descricao: pg.descricao }); setValorRecebido(""); }} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid " + C.accent, background: parteTarget && parteTarget.id === pg.id ? C.accent : C.surface, color: parteTarget && parteTarget.id === pg.id ? "#fff" : C.accent, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Cobrar</button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {(!temPartes || parteTarget) && (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                      {["dinheiro","cartao","pix"].map(m => (
                        <button key={m} onClick={() => setPayMethod(m)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid " + (payMethod === m ? C.accent : C.border), background: payMethod === m ? C.accent : C.surface, color: payMethod === m ? "#fff" : C.textMid, cursor: "pointer", fontSize: 13, fontWeight: 600, textTransform: "capitalize" }}>{m}</button>
                      ))}
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>A cobrar: <span style={{ color: C.gold }}>R$ {totalCobrar.toFixed(2)}</span></span>
                      <button onClick={() => { setValorRecebido(""); setShowPayModal(true); }} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: C.green, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Cobrar</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        );
      })()}

      {showPayModal && frente && (() => {
        const totalCobrar = parteTarget ? Number(parteTarget.valor) : Number(frente.order.total);
        const troco = (Number(valorRecebido) || 0) - totalCobrar;
        const podeConfirmar = payMethod !== "dinheiro" || (Number(valorRecebido) > 0 && troco >= 0);
        return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: C.card, borderRadius: 16, padding: 28, width: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: C.text, fontFamily: "'Playfair Display', serif", marginBottom: 4 }}>Cobrança — Pedido #{frente.order.order_number}</div>
            {parteTarget && <div style={{ fontSize: 13, color: C.textMid, marginBottom: 4 }}>{parteTarget.descricao || "Parte da conta"}</div>}
            <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 18, textTransform: "capitalize" }}>Pagamento em {payMethod}</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderRadius: 10, background: C.surface, marginBottom: 14 }}>
              <span style={{ fontSize: 14, color: C.textMid }}>Valor a pagar</span>
              <span style={{ fontSize: 24, fontWeight: 700, color: C.gold, fontFamily: "'Playfair Display', serif" }}>R$ {totalCobrar.toFixed(2)}</span>
            </div>
            {payMethod === "dinheiro" && (
              <div style={{ marginBottom: 14 }}>
                <input type="number" autoFocus placeholder="Valor recebido" value={valorRecebido} onChange={e => setValorRecebido(e.target.value)} style={{ width: "100%", boxSizing: "border-box", padding: "12px 14px", borderRadius: 10, border: "1px solid " + C.border, background: C.surface, fontSize: 18, marginBottom: 10 }} />
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderRadius: 10, background: troco >= 0 && Number(valorRecebido) > 0 ? C.greenSoft : C.accentSoft }}>
                  <span style={{ fontSize: 14, color: C.textMid }}>{troco >= 0 ? "Troco" : "Falta"}</span>
                  <span style={{ fontSize: 22, fontWeight: 700, color: troco >= 0 ? C.green : C.accent }}>R$ {Math.abs(Number(valorRecebido) > 0 ? troco : 0).toFixed(2)}</span>
                </div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowPayModal(false)} style={{ padding: "10px 20px", borderRadius: 8, border: "1px solid " + C.border, background: C.surface, color: C.textMid, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>Cancelar</button>
              <button onClick={handleConfirmPay} disabled={!podeConfirmar} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: podeConfirmar ? C.green : C.border, color: "#fff", fontWeight: 700, fontSize: 14, cursor: podeConfirmar ? "pointer" : "not-allowed" }}>Confirmar</button>
            </div>
          </div>
        </div>
        );
      })()}

      {sessDetail && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => setSessDetail(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.card, borderRadius: 16, padding: 26, width: 560, maxHeight: "80vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.text, fontFamily: "'Playfair Display', serif", marginBottom: 4 }}>Conferência do fechamento</div>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
              {new Date(sessDetail.session.opened_at).toLocaleString("pt-BR")} — {sessDetail.session.closed_at ? new Date(sessDetail.session.closed_at).toLocaleString("pt-BR") : "aberto"}
            </div>
            {[...(sessDetail.orders || []).map(o => ({ n: o.order_number, d: o.customer_name || "-", v: o.total, m: o.payment_method, h: o.paid_at })),
              ...(sessDetail.partes_mesa || []).map(p => ({ n: p.order_number, d: (p.descricao || "Parte") + " (mesa)", v: p.total, m: p.payment_method, h: p.paid_at }))]
              .sort((a, b) => new Date(a.h) - new Date(b.h))
              .map((r, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "50px 1fr 90px 70px 90px", gap: 8, alignItems: "center", padding: "8px 10px", borderRadius: 8, background: i % 2 ? C.surface : "transparent", fontSize: 13 }}>
                <span style={{ fontWeight: 700, color: C.text }}>#{r.n}</span>
                <span style={{ color: C.textMid }}>{r.d}</span>
                <span style={{ color: C.textMuted, fontSize: 12, textTransform: "capitalize" }}>{r.m || "-"}</span>
                <span style={{ color: C.textMuted, fontSize: 12 }}>{r.h ? new Date(r.h).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "-"}</span>
                <span style={{ fontWeight: 700, color: C.green, textAlign: "right" }}>R$ {Number(r.v).toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", borderTop: "2px solid " + C.border, marginTop: 12, paddingTop: 12 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Total da sessão</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: C.green, fontFamily: "'Playfair Display', serif" }}>R$ {Number(sessDetail.session.total_geral || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Payment breakdown */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 28 }}>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 16, fontFamily: "'Playfair Display', serif" }}>Por forma de pagamento</div>
          {[
            { label: "💵 Dinheiro", value: totalCash, color: C.green },
            { label: "💳 Cartão", value: totalCard, color: C.blue },
            { label: "📱 PIX", value: totalPix, color: C.accent },
          ].map((pm, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < 2 ? `1px solid ${C.borderLight}` : "none" }}>
              <span style={{ fontSize: 14, color: C.textMid }}>{pm.label}</span>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 80, height: 6, borderRadius: 3, background: C.surfaceAlt, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 3, background: pm.color, width: totalRevenue > 0 ? `${(pm.value / totalRevenue) * 100}%` : "0%", transition: "width 0.5s" }}/>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: C.text, minWidth: 80, textAlign: "right" }}>R$ {pm.value.toFixed(2)}</span>
              </div>
            </div>
          ))}
        </div>
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 16, fontFamily: "'Playfair Display', serif" }}>Resumo rápido</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "Subtotal produtos", value: totalRevenue - totalDeliveryFees },
              { label: "Taxas de entrega", value: totalDeliveryFees },
            ].map((r, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: C.textMid, padding: "4px 0" }}>
                <span>{r.label}</span><span style={{ fontWeight: 500 }}>R$ {r.value.toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "12px 0 0", borderTop: `2px solid ${C.border}`, marginTop: 4 }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Total do dia</span>
              <span style={{ fontSize: 20, fontWeight: 700, color: C.green, fontFamily: "'Playfair Display', serif" }}>R$ {totalRevenue.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pagos do dia */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 28 }}>
        <div style={{ padding: "16px 22px", borderBottom: `1px solid ${C.border}`, fontSize: 13, fontWeight: 600, color: C.text, fontFamily: "'Playfair Display', serif" }}>Pedidos concluídos</div>
        <div style={{ fontSize: 11, color: C.textMuted, display: "grid", gridTemplateColumns: "70px 1fr 80px 100px 80px 100px", padding: "10px 22px", borderBottom: `1px solid ${C.borderLight}`, textTransform: "uppercase", letterSpacing: 0.8 }}>
          <span>Pedido</span><span>Cliente</span><span>Tipo</span><span>Pagamento</span><span>Hora</span><span style={{ textAlign: "right" }}>Total</span>
        </div>
        {completed.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: C.textMuted, fontSize: 14 }}>Nenhum pedido concluído</div>
        ) : (
          completed.map(o => (
            <div key={o.id}>
              <div onClick={() => setExpandedOrder(expandedOrder === o.id ? null : o.id)} style={{ display: "grid", gridTemplateColumns: "70px 1fr 80px 100px 80px 100px", padding: "12px 22px", borderBottom: `1px solid ${C.borderLight}`, fontSize: 13, alignItems: "center", cursor: "pointer", background: expandedOrder === o.id ? C.surfaceAlt : "transparent" }}>
                <span style={{ fontWeight: 600, color: C.text }}>#{o.order_number}</span>
                <span style={{ color: C.textMid }}>{o.customer_name}</span>
                <span style={{ fontSize: 11, color: C.textMuted }}>{o.order_type === "mesa" ? "🍽️ Mesa" : o.order_type === "delivery" ? "🛵 Delivery" : "🏃 Retirada"}</span>
                <span style={{ fontSize: 12 }}>{o.pagamentos_mesa && o.pagamentos_mesa.length > 0 ? "📋 Dividido" : (PAYMENT_LABELS[o.payment_method] || o.payment_method)}</span>
                <span style={{ color: C.textMuted, fontSize: 12 }}>{o.paid_at ? new Date(o.paid_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "--"}</span>
                <span style={{ fontWeight: 700, color: C.green, textAlign: "right" }}>R$ {Number(o.total).toFixed(2)}</span>
              </div>
              {expandedOrder === o.id && (
                <div style={{ padding: "16px 22px 16px 40px", background: C.surfaceAlt, borderBottom: `1px solid ${C.borderLight}` }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginBottom: 8 }}>Itens:</div>
                  {o.items && o.items.map((item, idx) => (
                    <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.textMid, padding: "3px 0" }}>
                      <span>{item.quantity}x {item.name}</span>
                      <span>R$ {Number(item.item_total).toFixed(2)}</span>
                    </div>
                  ))}
                  {o.pagamentos_mesa && o.pagamentos_mesa.length > 0 && <>
                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text, marginTop: 12, marginBottom: 6 }}>Divisões de conta:</div>
                    {o.pagamentos_mesa.map((pg, idx) => (
                      <div key={idx} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.textMid, padding: "3px 0" }}>
                        <span>{pg.descricao} — {PAYMENT_LABELS[pg.payment_method] || pg.payment_method}</span>
                        <span style={{ color: pg.payment_status === "pago" ? C.green : C.accent }}>R$ {Number(pg.valor_total).toFixed(2)} {pg.payment_status === "pago" ? "✓" : "⏳"}</span>
                      </div>
                    ))}
                  </>}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal abrir caixa */}
      {showOpenModal && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
        <div style={{ background: C.card, borderRadius: 16, padding: 32, width: 380, border: `1px solid ${C.border}` }}>
          <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 20 }}>Abrir Caixa</h3>
          <label style={{ fontSize: 13, color: C.textMid, marginBottom: 6, display: "block" }}>Valor em caixa (troco inicial):</label>
          <input type="number" value={valorAbertura} onChange={e => setValorAbertura(e.target.value)} placeholder="0.00" style={{
            width: "100%", padding: "10px 14px", borderRadius: 8, border: `1px solid ${C.border}`,
            fontSize: 16, marginBottom: 20, background: C.surface, color: C.text
          }} />
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => setShowOpenModal(false)} style={{ flex: 1, padding: "10px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.surface, color: C.textMid, fontSize: 14, cursor: "pointer" }}>Cancelar</button>
            <button onClick={handleOpenCaixa} style={{ flex: 1, padding: "10px", borderRadius: 8, border: "none", background: C.green, color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" }}>Abrir</button>
          </div>
        </div>
      </div>}

    </div>
  );
  };
// MAIN APP
// ============================================
export default function MisePanel() {
  const [auth, setAuth] = useState(!!sessionStorage.getItem("mise_token"));
  const [activeTab, setActiveTab] = useState(() => (api.getUser()?.role === "cozinha" ? "cozinha" : "pedidos"));
  const [orders, setOrders] = useState([]);
  const wsRef = useRef(null);
  const playSound = useNotificationSound();

  const user = api.getUser();
  const tenant = api.getTenant();

  const loadOrders = useCallback(async () => {
    const data = await api.get("/api/orders");
    if (data) {
      data.sort((a, b) => b.order_number - a.order_number);
      setOrders(data);
    }
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (!auth) return;
    const token = api.getToken();
    if (!token) return;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsUrl = `${protocol}//${window.location.host}/ws?token=${token}`;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      ws.onopen = () => { console.log("Mise WS connected"); };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === "new_order") {
            playSound();
            loadOrders();
          } else if (msg.type === "order_updated") {
            loadOrders();
          }
        } catch (err) {}
      };
      ws.onclose = () => { setTimeout(connect, 3000); };
      ws.onerror = () => { ws.close(); };
      wsRef.current = ws;
    };

    connect();
    return () => { if (wsRef.current) wsRef.current.close(); };
  }, [auth, loadOrders, playSound]);

  // Load orders on auth
  useEffect(() => { if (auth) loadOrders(); }, [auth, loadOrders]);

  const handleLogin = (data) => { setAuth(true); if (api.getUser()?.role === "cozinha") setActiveTab("cozinha"); };

  const handleLogout = () => {
    sessionStorage.clear();
    setAuth(false);
    setOrders([]);
  };

  const activeBadge = orders.filter(o => !["entregue", "retirado", "servido", "cancelado", "finalizado"].includes(o.status)).length;

  if (!auth) return <LoginPage onLogin={handleLogin} />;

  return (
    <div style={{ display: "flex", height: "100vh", background: C.bg, fontFamily: "'DM Sans', sans-serif", overflow: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600;700&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        button { font-family: 'DM Sans', sans-serif; transition: all 0.2s; }
        button:hover { filter: brightness(0.95); }
        input:focus { border-color: ${C.accent} !important; outline: none; }
      `}</style>
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} activeBadge={activeBadge} onLogout={handleLogout} tenant={tenant} user={user} />
      <div style={{ flex: 1, overflow: "auto" }}>
        {activeTab === "cozinha" && <CozinhaTab orders={orders} loadOrders={loadOrders} />}
        {activeTab === "pedidos" && <PedidosTab orders={orders} setOrders={setOrders} loadOrders={loadOrders} tenant={tenant} />}
        {activeTab === "mesas" && <MesasTab />}
        {activeTab === "cardapio" && <CardapioTab />}
        {activeTab === "caixa" && <CaixaTab />}
        {activeTab === "equipe" && <EquipeTab />}
        {activeTab === "config" && <ConfigTab />}
      </div>
    </div>
  );
}
